import React from 'react';
import { UniversalForm } from '@/components/forms/UniversalForm';
import { bodyCompositionFormConfig } from '@/configs/forms/bodyCompositionFormConfig';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { useQueryClient } from '@tanstack/react-query';

interface BodyCompositionFormData {
  recorded_date: string;
  total_weight?: number;
  body_fat_percentage?: number;
  skeletal_muscle_mass?: number;
  bmr?: number;
  notes?: string;
}

export const UnifiedBodyCompositionForm: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const handleSubmit = async (data: BodyCompositionFormData) => {
    if (!user) throw new Error('User not authenticated');

    const bodyCompositionEntry = {
      user_id: user.id,
      recorded_date: data.recorded_date,
      total_weight: data.total_weight || null,
      body_fat_percentage: data.body_fat_percentage || null,
      skeletal_muscle_mass: data.skeletal_muscle_mass || null,
      bmr: data.bmr || null,
      notes: data.notes || null,
    };

    const { error } = await supabase
      .from('body_composition')
      .insert(bodyCompositionEntry);

    if (error) throw error;

    // Invalidate relevant queries
    queryClient.invalidateQueries({ queryKey: ['body-composition'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
  };

  const initialData: Partial<BodyCompositionFormData> = {
    recorded_date: new Date().toISOString().split('T')[0],
  };

  return (
    <UniversalForm
      formId="body-composition-entry"
      title="Body Composition Entry"
      description="Track your body composition metrics and progress over time"
      config={bodyCompositionFormConfig}
      onSubmit={handleSubmit}
      initialData={initialData}
      persistState={true}
      className="max-w-2xl mx-auto"
    />
  );
};